// client/src/pages/Payment.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CreditCard, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import OnboardGuard from '../components/OnboardGuard';

/** Local UI components */
const Logo = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'h-12',
    md: 'h-20',
    lg: 'h-64',
    xl: 'max-h-[420px]',
  };
  return (
    <img
      src="/STB_LOGO_V1.png"
      alt="SecureTheBag Logo"
      className={`${sizes[size] || sizes.md} w-auto object-contain ${className}`}
    />
  );
};

const Footer = () => (
  <footer className="mt-auto py-4 text-center text-sm text-gray-500">
    © {new Date().getFullYear()} SecureTheBag
  </footer>
);

function PaymentForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qpPriceId = searchParams.get('priceId');
  const priceId = qpPriceId || sessionStorage.getItem('selectedPriceId');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirected, setRedirected] = useState(false);

  useEffect(() => {
    if (redirected) return;
    if (!priceId) {
      setError('Missing plan or user info.');
      return;
    }
    setLoading(true);
    setRedirected(true);
    api
      .post('/api/payments/create-checkout-session', { priceId })
      .then((res) => {
        const { checkoutUrl } = res.data;
        window.location.href = checkoutUrl;
      })
      .catch(err => {
        console.error('[PAYMENT.JSX] Failed to create checkout session:', err.response?.data || err);
        setError('Failed to initiate payment. Please try again.');
        setLoading(false);
        setRedirected(false);
      });
  }, [priceId, redirected]);

  const handleCancel = () => {
    sessionStorage.removeItem('selectedPriceId');
    navigate('/choose-plan', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block mb-6">
                <Logo size="md" />
              </div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h1>
              <p className="text-gray-600">Redirecting you to secure checkout...</p>
            </div>

            {/* Loading Animation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-8">
              <div className="flex flex-col items-center justify-center">
                <Loader2 className="w-12 h-12 text-green-600 animate-spin mb-4" />
                <p className="text-gray-600 text-center">Please wait while we prepare your checkout session</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block mb-6">
                <Logo size="md" />
              </div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Error</h1>
            </div>

            {/* Error Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-6">
                <p className="text-red-800 text-center">{error}</p>
              </div>
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="w-full min-h-[44px] px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Go Back to Plan Selection
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return null;
}

// User remains at 'choose_plan' step until Stripe webhook advances them to 'connect_bank'.
// Payment is transient UI — just a redirect to Stripe.
export default function Payment() {
  return (
    <OnboardGuard currentStep="choose_plan">
      <PaymentForm />
    </OnboardGuard>
  );
}
